import type React from 'react';

import { Typography } from '@mui/material';
import {
  OrdCreateInDialogButton,
  OrdEditInDialogButton,
  OrdFormList,
  OrdIconStandard,
  OrdReferenceInput,
  useIsReadonly,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { CallflowReadFields, HuntingGroupFields, PermissionFields, UserReadFields } from '@ragu-components/ord-types';
import { RecordContextProvider, ReferenceManyField, WrapperField, useTranslate } from 'react-admin';

import { HuntingGroupList } from '../huntingGroups/HuntingGroupList';
import { HuntingGroupsForm } from '../huntingGroups/HuntingGroupsForm';

import type { UserRead } from '@ragu-components/ord-types';

export const UserMobilityTab = ({ user }: { user: UserRead }): React.ReactElement => {
  const translate = useTranslate();
  const readonly = useIsReadonly();

  const createButton = (
    <OrdCreateInDialogButton inline fullWidth>
      <HuntingGroupsForm embedded defaultValues={{ owner: user.id }} />
    </OrdCreateInDialogButton>
  );

  const editButton = (
    <OrdEditInDialogButton mutationMode='pessimistic' fullWidth>
      <HuntingGroupsForm embedded />
    </OrdEditInDialogButton>
  );

  const emptyMessage = (
    <Typography color='text.secondary' sx={{ p: 2 }}>
      {translate('ra.navigation.no_results')}
    </Typography>
  );

  return (
    <RecordContextProvider value={user}>
      <OrdFormList icon={<OrdIconStandard name='mobilityConfigurations' />}>
        {/* No filter for mobility_configurations, because we are fetching by current session labels */}
        <OrdReferenceInput
          resource={Resources.mobility_configurations}
          source={UserReadFields.mobility_configuration}
          label='resources.users.fields.mobility_configuration'
          filter={{
            active: 'fiql',
            fiql: `(${CallflowReadFields.latest_callflow_version}.id=isnull=true)`,
          }}
        />
      </OrdFormList>
      <OrdFormList icon={<OrdIconStandard name='huntingGroups' />}>
        <Typography variant='caption' color={readonly ? 'text.disabled' : undefined} sx={{ pt: 1, display: 'block' }}>
          {translate('resources.hunting_groups.name')}
        </Typography>
      </OrdFormList>
      {/* ReferenceManyField is not working in the OrdFormList, so we put it outside */}
      <ReferenceManyField target={HuntingGroupFields['owner.id']} reference={PermissionFields.hunting_groups}>
        <HuntingGroupList
          embeddedChildren={<WrapperField label={createButton}>{editButton}</WrapperField>}
          layout='embedded'
          empty={readonly ? emptyMessage : createButton}
        />
      </ReferenceManyField>
    </RecordContextProvider>
  );
};
